/**
 * 🛡️ QRCODE MODAL MEJORADO - BASADO EN WHATICKET5
 * 
 * Este componente maneja el QR Code de forma segura
 * evitando errores que bloqueen la interfaz
 */

import React, { useEffect, useState, useContext } from "react";
import { QRCodeSVG } from "qrcode.react";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogContent, Paper, Typography, Box, Button, Divider } from "@material-ui/core";
import { FileCopy as CopyIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { getWhatsapp } from "../../services/whatsapp.api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  pairingCodeContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
  },
  pairingCodeText: {
    fontSize: "2rem",
    fontWeight: "bold",
    letterSpacing: "0.5rem",
    color: theme.palette.primary.main,
    fontFamily: "monospace",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  copyButton: {
    marginTop: theme.spacing(1),
  },
}));

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    // ✅ LIMPIAR QR CUANDO SE ABRE EL MODAL (para evitar mostrar QR viejo)
    if (open && whatsAppId) {
      setQrCode("");
      setPairingCode("");
    }
  }, [open, whatsAppId]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId || !open) return; // ✅ Solo fetch si el modal está abierto

      try {
        // ✅ Pequeño delay para dar tiempo al backend a generar el QR después de la solicitud
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data } = await getWhatsapp(whatsAppId);
        
        // ✅ SOLO ACTUALIZAR SI HAY QR CODE
        // Los eventos de socket se encargarán de actualizar cuando el QR esté listo
        if (data.qrcode) {
          setQrCode(data.qrcode);
        }
        if (data.pairingCode) {
          setPairingCode(data.pairingCode);
        }
        
        // ✅ NO CERRAR EL MODAL AQUÍ - Los eventos de socket se encargarán
      } catch (err) {
        // ✅ Manejar errores de red/timeout de forma silenciosa
        // Los eventos de socket se encargarán de actualizar cuando el QR esté listo
        // Solo loguear errores críticos (no 404, 504, CORS que son temporales)
        if (err.response && err.response.status >= 500) {
          console.warn("Error obteniendo sesión WhatsApp (temporal):", err.message);
        }
        // No mostrar error al usuario - los eventos de socket traerán el QR cuando esté listo
      }
    };
    
    if (open && whatsAppId) {
      fetchSession();
    }
  }, [whatsAppId, open]);

  useEffect(() => {
    if (!whatsAppId || !socket || !user.companyId) return;
    
    const companyId = user.companyId;

    // ✅ MANEJO SEGURO DE EVENTOS COMO WHATICKET5
    const onWhatsappData = (data) => {
      try {
        // ✅ CIERRE INMEDIATO SI SE RECIBE EVENTO DE CONEXIÓN EXITOSA
        if (data.action === "connected" && data.whatsapp && data.whatsapp.id === whatsAppId) {
          
          // ✅ ACTUALIZAR ESTADO LOCAL INMEDIATAMENTE
          if (window.updateWhatsAppStatus) {
            const updateEvent = new CustomEvent('updateWhatsAppStatus', {
              detail: {
                whatsAppId: whatsAppId,
                newStatus: {
                  status: "CONNECTED",
                  qrcode: "",
                  updatedAt: new Date().toISOString()
                }
              }
            });
            window.dispatchEvent(updateEvent);
          }
          
          // ✅ FORZAR ACTUALIZACIÓN DE LA LISTA DE CONEXIONES
          if (window.refreshConnections) {
            const refreshEvent = new CustomEvent('refreshConnections');
            window.dispatchEvent(refreshEvent);
          }
          
          // ✅ CERRAR MODAL INMEDIATAMENTE
          onClose();
          return;
        }
        
        // ✅ VERIFICACIÓN SEGURA: Verificar que data.session existe y tiene id
        if (data.action === "update" && data.session && data.session.id === whatsAppId) {
          // ✅ MANEJAR ERRORES INFORMATIVOS (QR_EXPIRED, MAX_QR_RETRIES_REACHED)
          // ⚠️ NOTA: Solo cerrar modal aquí, NO mostrar toast (ya se muestra en Connections para evitar duplicados)
          if (data.error) {
            if (data.error.code === "QR_EXPIRED" || data.error.code === "MAX_QR_RETRIES_REACHED") {
              // Cerrar modal inmediatamente (el toast ya se muestra en Connections)
              onClose();
              return;
            }
          }
          
          // ✅ ACTUALIZAR QR CODE Y PAIRING CODE SI ESTÁN DISPONIBLES
          if (data.session.qrcode) {
            setQrCode(data.session.qrcode);
          }
          if (data.session.pairingCode) {
            setPairingCode(data.session.pairingCode);
          }
          
          // ✅ SOLO CERRAR SI ESTÁ REALMENTE CONECTADO (no si está en PAIRING o qrcode)
          const isWaitingForQR = data.session.status === "PAIRING" || 
                                data.session.status === "qrcode" || 
                                data.session.status === "OPENING";
          
          if (data.session.status === "CONNECTED" && !isWaitingForQR) {
            onClose();
            return;
          }
        }
        
        // ✅ MANEJO ALTERNATIVO: Si los datos vienen en data.whatsapp
        if (data.action === "update" && data.whatsapp && data.whatsapp.id === whatsAppId) {
          // ✅ ACTUALIZAR QR CODE Y PAIRING CODE SI ESTÁN DISPONIBLES
          if (data.whatsapp.qrcode) {
            setQrCode(data.whatsapp.qrcode);
          }
          if (data.whatsapp.pairingCode) {
            setPairingCode(data.whatsapp.pairingCode);
          }
          
          // ✅ SOLO CERRAR SI ESTÁ REALMENTE CONECTADO (no si está en PAIRING o qrcode)
          // NO cerrar si está en proceso de generar QR (PAIRING, qrcode, OPENING)
          const isWaitingForQR = data.whatsapp.status === "PAIRING" || 
                                 data.whatsapp.status === "qrcode" || 
                                 data.whatsapp.status === "OPENING";
          
          if (data.whatsapp.status === "CONNECTED" && !isWaitingForQR) {
            // ✅ ACTUALIZACIÓN INMEDIATA DE LA INTERFAZ
            const updateEvent = new CustomEvent('updateWhatsAppStatus', {
              detail: {
                whatsAppId: whatsAppId,
                newStatus: {
                  status: "CONNECTED",
                  qrcode: "",
                  updatedAt: new Date().toISOString()
                }
              }
            });
            window.dispatchEvent(updateEvent);
            
            const refreshEvent = new CustomEvent('refreshConnections');
            window.dispatchEvent(refreshEvent);
            
            onClose();
            return;
          }
          
          // ✅ SI ESTÁ EN PAIRING O QRCODE, ESPERAR A QUE LLEGUE EL QR
          // NO cerrar, solo esperar
        }
      } catch (error) {
        // ✅ NO PROPAGAR EL ERROR PARA EVITAR BLOQUEO
      }
    };

    // ✅ VERIFICAR QUE SOCKET TIENE EL MÉTODO 'on' ANTES DE USARLO
    if (socket && typeof socket.on === 'function') {
      // ✅ REGISTRAR LISTENER DE FORMA SEGURA PARA AMBOS EVENTOS
      socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);
      socket.on(`company-${companyId}-whatsapp`, onWhatsappData);

      return () => {
        // ✅ CLEANUP AUTOMÁTICO CON VERIFICACIÓN
        if (socket && typeof socket.off === 'function') {
          socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
          socket.off(`company-${companyId}-whatsapp`, onWhatsappData);
        }
      };
    }
  }, [whatsAppId, onClose, user.companyId, socket, open]);

  const handleCopyPairingCode = () => {
    if (pairingCode) {
      navigator.clipboard.writeText(pairingCode);
      toast.success("Código de emparejamiento copiado al portapapeles");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" scroll="paper">
      <DialogContent>
        <Paper elevation={0}>
          <Typography color="secondary" gutterBottom>
            {i18n.t("qrCode.message")}
          </Typography>
          <div className={classes.root}>
            {qrCode ? (
              <QRCodeSVG value={qrCode} size={300} style={{ backgroundColor: "white", padding: '5px' }} />
            ) : (
              <span>{i18n.t("qrCode.waiting")}</span>
            )}
          </div>
          
          {pairingCode && (
            <>
              <Divider style={{ margin: "20px 0" }} />
              <Box className={classes.pairingCodeContainer}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  O usa el código de emparejamiento:
                </Typography>
                <Typography className={classes.pairingCodeText}>
                  {pairingCode}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyPairingCode}
                  className={classes.copyButton}
                >
                  Copiar Código
                </Button>
                <Typography variant="caption" display="block" style={{ marginTop: 16 }} color="textSecondary">
                  1. Abre WhatsApp en tu teléfono<br />
                  2. Ve a Configuración → Dispositivos vinculados<br />
                  3. Selecciona "Vincular un dispositivo"<br />
                  4. Toca "Vincular con número de teléfono en su lugar"<br />
                  5. Ingresa este código
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);